import {Component, OnInit, Output, EventEmitter, Input, OnChanges, SimpleChanges, ElementRef, AfterViewInit, OnDestroy, ViewChildren, QueryList} from '@angular/core';
import {Donation} from "../donation/donation";
import {ShopCartService} from "./shop.cart.service";
import {Product} from "./shop.product.model";
import {Bundle} from "./shop.bundle.model";
import {DecimalPipe} from "@angular/common";
import {UrlService} from "../url/url.service";
import { OptionService } from 'option/option.service';
import { ShoplistService } from 'service/shoplist.service';
import { ShopListItemComponent } from './shop.list.item.component';
import { BundlesService } from 'service/bundles.service';
import { ShopProductService } from './shop.product.service';

declare let window: any;
declare let __twingle_products: any;
declare let __twingle_categories: any;
declare let attachEvent: any;

@Component({
    selector: 'shop-list',
    templateUrl: './shop.list.component.html',
    providers: [DecimalPipe, OptionService]
})
export class ShopListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChildren(ShopListItemComponent, { read: ElementRef }) shopListItems: QueryList<ElementRef>;

    /**
     * The current donation
     */
    @Input() donation: Donation;

    /**
     * The current widget size
     */
    @Input() size: string;

    /**
     * Output parameter for changing the step
     * @type {EventEmitter}
     */
    @Output() stepChange: EventEmitter<string> = new EventEmitter();

    /**
     * Eventhandler for showing a single product.
     */
    @Output() onShowItem: EventEmitter<Product | Bundle> = new EventEmitter();

    /**
     * Eventhandler for showing the download of a product.
     */
    @Output() onShowDownloadForProduct: EventEmitter<Product> = new EventEmitter();

    /**
     * list of available items
     */
    items: Array<Product | Bundle> = [];

    /**
     * total purchasable items
     */
    totalVisibleItems: Array<Product | Bundle> = [];

    /**
     * List of purchasable items filtered by category
     */
    currentlyVisibleItems: Array<Product | Bundle> = [];

    /**
     * list of available categories
     */
    categories: {id: number, name: string, product_count: number}[];

    /**
     * the currently selected category
     */
    currentCategory: number | null = null;

    /**
     * List of placeholders for the button to show the chart.
     */
    buttonPlaceholder: {items: number, price: string, currency: string} = {items: 0, price: '0', currency: ''};

    /**
     * the currently pisible page in the items pagination
     */
    page = 1;

    /**
     * number of items we want to show per page in the item pagination
     */
    itemsPerPage = 0;

    /**
     * The available screensizes we want to check for
     */
    itemsPerPageAndSize: any = {
        small: 8,
        medium: 8,
        large: 9,
        xlarge: 12
    }

    /**
     * Toggle for showing the categories as DropDown
     */
    showCategoryDropDown = false;

    /**
     * shop list button element
     */
    shopListButton: any = null;
    /**
     * sticky wrapper element
     */
    stickyWrapper: any = null;

    /**
     * Toggle if sticky start threshold has been crossed
     */
    isAfterStickyStartThreshold = false;
    /**
     * Toggle if sticky end threshold has been crossed
     */
    isBeforeStickyEndThreshold = false;
    /**
     * shop list button position
     */
    buttonPositions = 0;
    /**
     * shop list bigger than screen size
     */
    isShopSmallerThanScreen = false;

    /**
     * constructor
     * @param cartService
     * @param decimalPipe
     * @param urlService
     * @param elementRef
     * @param shoplistService
     * @param shopProductService
     * @param bundleService
     * @param optionService
     */
    constructor(private cartService: ShopCartService, private decimalPipe: DecimalPipe, private urlService: UrlService, private elementRef: ElementRef,
                private shoplistService: ShoplistService, private bundleService: BundlesService, private shopProductService: ShopProductService,
                private optionService: OptionService) {}

    /**
     * @inheritDoc
     */
    ngOnInit(): void {
        // get products and categories to show
        if (typeof  __twingle_products !== "undefined" && __twingle_products.length > 0) {
          // map whole list to real objects and filter on the available

          this.items = __twingle_products.map(data => {
            if (data.type === 'bundle') {
              const bundle = new Bundle();
              Object.assign(bundle, data);
              this.bundleService.prepareBundleInformation(bundle);
              return bundle;
            }

            if (data.type === 'product') {
              const product = new Product();
              Object.assign(product, data);
              return product;
            }
          }).filter((item: Product | Bundle) => {
            // check if product is available for purchase
            // if it is a bundle, it is purchasable if all products in the bundle are purchasable
            const isPurchasable = item instanceof Product ? item.checkIfPurchasable() : true;

            // Check if the product does not have any digitial downloads
            const hasDigitalDownloads = (item instanceof Product) && item.file !== null;

            // return the product if any of the above conditions are false
            return isPurchasable || hasDigitalDownloads;
          });
        }

        // should we hide products that are parts of bundles?
        if (this.optionService.getOption('has_shop_hide_bundleproducts') === true) {
          const bundles = this.items.filter((item: Product | Bundle) => item instanceof Bundle).map((item: Bundle) => item.bundle_products);
          const productIdsInBundles = [].concat(...bundles).map(item => item.id);

          // remove products from items that are part of a bundle
          this.items = this.items.filter(item => !(item instanceof Product && productIdsInBundles.includes(item.id)));
        }

        this.categories = [];
        if (typeof  __twingle_categories !== "undefined" && __twingle_categories.length > 0) {
            this.categories = __twingle_categories;
        }

        // initialize the category
        this.setCategory(null);

        // check if a special category should be visible on start
        const categoryId: string = this.urlService.findGetParameter(this.urlService.PARAMETER_CATEGORY);
        if (categoryId && this.categories.length > 0) {
            // try to find the product and show if if it is available
            const category = this.categories.find(element => element.hasOwnProperty('id') && element.id === parseInt(categoryId, 10));
            if (category) {
                this.setCategory(category.id);
            }
        }

        // init the list of placeholders
        this.updatePlaceHolderAndAmount();

        // on changes in the chart, we need to do a few things.
        this.cartService.itemsUpdated.subscribe(() => this.updatePlaceHolderAndAmount());

        // set items per page initially
        this.updateItemsPerPage();

        // check if the selection of the categoriy should be via dropdown or buttons
        this.checkCategorySelection();

        // set the page to the last loaded page
        this.page = this.shoplistService.getLastShopPageLoaded();

        // updateCategories
        this.updateCategories();
    }

    /**
     * @inheritDoc
     */
    ngAfterViewInit(): void {
        // only do this if the shop is embedded
        if (this.isEmbedded()) {
            this.shopListButton = this.elementRef.nativeElement.querySelector('.shop-product-list-buttons');
            this.stickyWrapper = this.elementRef.nativeElement.querySelector('.sticky-wrapper');

            // bind message receiver
            this.handleResize = this.handleResize.bind(this);
            this.handleMessage = this.handleMessage.bind(this);

            if (window.addEventListener) {
                window.addEventListener("resize", this.handleResize, false);
                window.addEventListener("message", this.handleMessage, false);
            } else {
                attachEvent("onresize", this.handleResize);
                attachEvent("onmessage", this.handleMessage);
            }
        } else {
            // if the shop is not embedded, scroll to the last visited item
            this.scrollToLastVisitedItem();
        }
    }

    /**
     * @inheritDoc
     */
    ngOnDestroy(): void {
        // only do this if the shop is embedded
        if (this.isEmbedded()) {
            if (window.removeEventListener) {
                window.removeEventListener("resize", this.handleResize, false);
                window.removeEventListener("message", this.handleMessage, false);
            } else {
                window.detachEvent("onresize", this.handleResize);
                window.detachEvent("onmessage", this.handleMessage);
            }
        }
    }

    /**
     * handle resize events
     */
    handleResize(): void {
        // retrigger trigger scroll handler in the parent window once to set the correct position after transition
        parent.postMessage({
            "type": "retriggerScrollHandler",
            "cid": OptionService.getConatinerId()
        }, "*");

        this.stickButtonsToBottom();
        this.scrollToLastVisitedItem();
    }

    /**
     * handles the incoming messages from the parent window
     * @param event
     */
    handleMessage(event: MessageEvent): void {
        if (event.data.type === 'scroll') {
            this.handleParentScroll(event.data);
        }
    }

    /**
     * handles the incoming messages from the parent window
     * @param event
     */
    handleEmbeddedMessage(event: any): void {
        if (event.data.type === 'scroll') {
            this.handleParentScroll(event.data);
        }
    }

    /**
     * handle scroll events from the parent window
     * @param event
     */
    handleParentScroll(event: { iFrameHeight: number, iFrameOffset: number, type: string, scrollY: number, innerHeight }): void {
        this.calculateButtonPositions(event);
        this.stickButtonsToBottom();
    }

    /**
     * calculate the sticky button positions and thresholds
     * @param event
     */
    calculateButtonPositions(event: { iFrameHeight: number, iFrameOffset: number, type: string, scrollY: number, innerHeight }): void {
        if (event.iFrameHeight < event.innerHeight) {
            this.isShopSmallerThanScreen = true;
            return;
        } else {
            this.isShopSmallerThanScreen = false;
        }
        // get the relevant measurements
        const shopListButtonOffset = this.shopListButton.getBoundingClientRect().top;
        const shopListButtonHeight = this.shopListButton.getBoundingClientRect().height;
        const offset = 100; // offset for the sticky start threshold (top of the screen)

        // check if the sticky wrapper should be sticky
        this.isAfterStickyStartThreshold = event.iFrameOffset - event.innerHeight + shopListButtonHeight + offset < 0;
        this.isBeforeStickyEndThreshold = shopListButtonOffset + shopListButtonHeight + event.iFrameOffset - event.innerHeight > 0;
        this.buttonPositions = -1 * (event.iFrameOffset - event.innerHeight + shopListButtonHeight + shopListButtonOffset);
    };

    /**
     * position the sticky buttons to the bottom of the screen
     */
    stickButtonsToBottom(): void {
        // only do this if there is at least one product in the basket
        if (!this.stickyWrapper || !this.cartService || this.cartService?.getOverallCount() === 0 || this.isShopSmallerThanScreen) {
            return;
        }
        if (this.isAfterStickyStartThreshold && this.isBeforeStickyEndThreshold) {
            this.stickyWrapper.style.top = this.buttonPositions + 'px';
            this.stickyWrapper.style.position = 'relative';
            this.stickyWrapper.classList.add('is-embedded-sticky');
        } else {
            this.stickyWrapper.style.top = '0px';
            this.stickyWrapper.style.position = 'unset';
            this.stickyWrapper.classList.remove('is-embedded-sticky');
        }
    }

    /**
     * decide if the category selection should be visible as buttons or drodown depending on the number of categories and the screensize
     */
    checkCategorySelection(): void {
        this.showCategoryDropDown = this.size === "small" || this.size === "medium" || this.categories.length > 4;
    }

    /**
     * @inheritDoc
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty("size") && changes.size.isFirstChange() === false) {
            // on size change, change the number of visible items.
            this.updateItemsPerPage();
            this.checkCategorySelection();
        }
    }

    /**
     * Updates the items per page that we want to show
     */
    updateItemsPerPage(): void {
        if (this.itemsPerPageAndSize.hasOwnProperty(this.size)) {
            this.itemsPerPage = this.itemsPerPageAndSize[this.size];
        }
    }

    /**
     * Updates the placeholder data for the button and its teaser.
     */
    updatePlaceHolderAndAmount(): void {
        this.shopProductService.getDecimalsShownCart().subscribe((decimalsShown) => {
            // update placeholder as whole item because change detection will not work otherwise
            const priceFormat = decimalsShown ? '1.2-2' : '1.0-0';
            this.buttonPlaceholder = {
                items: this.cartService.getOverallCount(),
                price: this.decimalPipe.transform(this.cartService.getPrice(), priceFormat),
                currency: this.donation.currencySymbol
            };
            // update the price of the donation
            this.donation.amount = this.cartService.getPrice();
        });
    }

    /**
     * set/change the currently selected category
     * @param categoryId
     */
    setCategory(categoryId: any = null): void {
        this.currentCategory = parseInt(categoryId);

        if (this.currentCategory === null || isNaN(this.currentCategory)) {
            this.currentCategory = null;
        }

        this.calculateVisibleItems();
    }

    /**
     * change the current step because of cart or details is called.
     * @param newStep
     */
    changeStep(newStep: string): void {

        this.stepChange.emit(newStep);
    }

    /**
     * if an item is set/added to the chart, call this function.
     * @param item
     */
    addItemToChart(item: {product: Product, count: number, increaseCountIfProductsExists: boolean,
      freePrice: number, text: string, forceCountModifiable?: boolean}): void {
        this.cartService.addItem(item.product, item.count, item.increaseCountIfProductsExists, item.freePrice, item.text, item.forceCountModifiable);

        if (this.isEmbedded()) {
            this.stickButtonsToBottom();
        }
    }

    /**
     * fire the event emitter for showing a product.
     * @param item
     */
    showItem(item: Product | Bundle): void {
        this.shoplistService.setLastShopItemVisitedId(item.id);
        this.onShowItem.emit(item);
    }

    /**
     * fire the event emitter for showing the download of a product.
     */
    showDownloadForProduct(product: Product): void {
        this.shoplistService.setLastShopItemVisitedId(product.id);
        this.onShowDownloadForProduct.emit(product);
    }

    /**
     * checks if the widget is embeded or not.
     */
    isEmbedded(): boolean {
        return window.parent && window !== window.parent;
    }

    /**
     * load next page of products
     */
    showNextPage(): void {
        this.page++;

        this.shoplistService.setLastShopPageLoaded(this.page);
    }

    /**
     * scroll to the last visited item if there is one
     */
    scrollToLastVisitedItem(): void {
        // scroll to the last visited product
        const lastVisitedId = this.shoplistService.getLastShopItemVisitedId();

        if (lastVisitedId && this.shopListItems && this.shopListItems.length > 0) {
            const lastVisitedProductItem = this.shopListItems.find(item => {
                return item.nativeElement.getAttribute('data-product-id') === lastVisitedId.toString();
            });

            if (lastVisitedProductItem) {
                // Use the ElementRef to get the native element and scroll into view
                if (this.isEmbedded()) {
                    parent.postMessage({
                        "type": "scrollToTop",
                        "value": lastVisitedProductItem.nativeElement.getBoundingClientRect().top,
                        "cid": OptionService.getConatinerId(),
                    }, "*");
                } else {
                    // we need the timeout to wait until all items are fully rendedered (images)
                    setTimeout(() => {
                        lastVisitedProductItem.nativeElement.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                }
                this.shoplistService.setLastShopItemVisitedId(null);
            }
        }
    }

    /**
     * refresh the items
     */
    calculateVisibleItems(): void {
        // we use a timeout to defer the change detection to the next cycle
        setTimeout(() => {
            // filter out non purchasable items
            const totalVisibleItems = this.items.filter((item: Product | Bundle) => {
                // check if product is available for purchase or has a file to download
                if (item instanceof Product) {
                    return item.isPurchasable || item.file
                }
                return  item.isPurchasable;
            });

            // filter out items that are not in the current category
            const currentlyVisibleItems = totalVisibleItems.filter((item: Product | Bundle) =>
                this.currentCategory === null || item.categories.includes(this.currentCategory)
            );

            // always reset the items
            this.totalVisibleItems = totalVisibleItems;
            this.currentlyVisibleItems = currentlyVisibleItems;

            // update the categories
            this.updateCategories();
        }, 0);
    }

    /**
     * updated the categories
     */
    updateCategories(): void {
        this.categories = this.categories.map((category) => {
            category.product_count = this.totalVisibleItems.filter((item) => item.categories.includes(category.id)).length;
            return category;
        });
    }
}
